import AdvanceTable from "components/common/advance-table/AdvanceTable";
import TablePaginationFooter from "components/common/TablePaginationFooter"; // Switched to the modern footer
import CardDropdown from "components/common/CardDropdown";
import { useAdvanceTable } from "hooks";
import useRoles from "hooks/access-control/useRoles";
import AdvanceTableProvider from "providers/AdvanceTableProvider";
import React, { useState, useMemo, useCallback } from "react";
import { Dropdown, Spinner } from "react-bootstrap";
import WidgetSectionTitle from "widgets/WidgetSectionTitle";
import { Empty } from "antd";
import IconButton from "components/common/IconButton";
import { ConfirmDeleteModal } from "components/common/ConfirmDeleteModal";
import CreateOrUpdateRoleModal from "./CreateOrUpdateRoleModal";
import { useNavigate } from "react-router-dom";
import { useAppContext } from "providers/AppProvider";
import Guard from "../rbac/Guard";
import { PRIV } from "../rbac/privileges";

const customStyles = {
  backgroundColor: "#e8f7e3",
  color: "#2e7d32",
  padding: "3px 8px",
  borderRadius: "6px",
  fontSize: "0.65rem",
  border: "1px solid #d5ebd0",
  cursor: "pointer",
  fontWeight: 600,
};

export default function RolesManagement() {
  const navigate = useNavigate();
  const { roles, addRole, updateRole, loading, deleteRoleById } = useRoles();
  const { config } = useAppContext();
  const isCollapsed = config.isNavbarVerticalCollapsed;

  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState(null);
  const [deletingRole, setDeletingRole] = useState(null);

  const formatText = (text) => `${text.slice(0, 25)}...`;

  const handleUpdateRole = useCallback((id) => {
    const role = roles.find((r) => r.id === id);
    setSelectedRole(role);
    setShowUpdateModal(true);
  }, [roles]);

  const confirmDelete = useCallback((id) => {
    setRoleToDelete(id);
    setShowDeleteModal(true);
  }, []);

  const handleDelete = async () => {
    try {
      setDeletingRole(roleToDelete);
      await deleteRoleById(roleToDelete);
    } finally {
      setDeletingRole(null);
      setShowDeleteModal(false);
    }
  };

  const columns = useMemo(() => [
    {
      accessorKey: "name",
      header: "Name",
      meta: {
        headerProps: { style: { width: '220px' }, className: "text-900 fw-bold" }, // Standard Bold Header
      },
      cell: ({ getValue }) => {
        const val = getValue() || "";
        return (
          <h6
            className="mb-0 text-900 fs-10 text-truncate d-inline-block fw-normal" // Kept Bold
            style={{ maxWidth: 220 }}
            title={val}
          >
            {val}
          </h6>
        );
      },
    },
    {
      accessorKey: "description",
      header: "Description",
      meta: {
        headerProps: { style: { width: '250px' }, className: "text-900 fw-bold" }, // Standard Bold Header
      },
      cell: ({ getValue }) => {
        const desc = getValue() || "No description provided";
        const displayText = desc.length > 25 ? formatText(desc) : desc;
        return (
          <span
            className="text-500 d-inline-block fw-normal" // Changed to normal weight
            style={{ fontSize: 12, lineHeight: 1.4, cursor: 'help' }} // Standardized size
            title={desc}
          >
            {displayText}
          </span>
        );
      },
    },
    {
      accessorKey: "owner",
      header: "Owner",
      meta: {
        headerProps: { style: { width: '240px' }, className: "text-900 fw-bold" },
      },
      cell: ({ getValue, row }) => {
        const app = getValue() || row.original?.owner || "-";
        return (
          <span
            className="text-500 d-inline-block fw-normal text-truncate"
            style={{ fontSize: 12, lineHeight: 1.4, maxWidth: 240 }}
            title={app}
          >
            {app}
          </span>
        );
      },
    },
    {
      accessorKey: "privileges",
      header: "Privileges",
      meta: {
        headerProps: { className: "text-900 fw-bold" }, // Standard Bold Header
      },
      cell: ({ getValue, row }) => {
        const privileges = getValue() || [];
        const limit = isCollapsed ? 9 : 5;
        if (privileges.length === 0) return <span className="fs-10 text-400 fw-bold">No privileges assigned</span>;

        const displayed = privileges.slice(0, limit);
        const remaining = privileges.length - limit;
        return (
          <div className="d-flex flex-wrap gap-1">
            {displayed.map((privilege) => (
              <span key={privilege.id} style={{ ...customStyles, fontWeight: 500 }}>
                {privilege.name}
              </span>
            ))}
            {remaining > 0 && (
              <span
                style={{
                  ...customStyles,
                  backgroundColor: "#fff3cd",
                  color: "#b7791f",
                  border: "1px solid #ffe8a1",
                  fontWeight: 600, // Kept "more" indicator slightly heavier for visibility
                }}
                onClick={() => navigate(`/access-control/roles/${row.original.id}`)}
              >
                +{remaining} more
              </span>
            )}
          </div>
        );
      },
    },
    {
      id: "actions",
      header: "Actions",
      meta: {
        headerProps: { style: { width: '50px' }, className: "text-900 text-center fw-bold" }, // Standard Bold Header
        cellProps: { className: "text-center" },
      },
      cell: ({ row }) => {
        const item = row.original;
        return (
          <CardDropdown drop="start">
            <div className="py-2 fw-normal"> {/* Ensure dropdown text is normal weight */}
              <Dropdown.Item className="fs-10" onClick={() => navigate(`/access-control/roles/${item.id}`)}>
                View
              </Dropdown.Item>
              <Guard privilege={PRIV.UPDATE_ROLES}>
                <Dropdown.Item className="fs-10" onClick={() => handleUpdateRole(item.id)}>
                  Edit
                </Dropdown.Item>
              </Guard>
              <Guard privilege={PRIV.DELETE_ROLES}>
                <Dropdown.Divider as="div" />
                <Dropdown.Item
                  className="fs-10 text-danger"
                  onClick={() => confirmDelete(item.id)}
                >
                  Delete
                </Dropdown.Item>
              </Guard>
            </div>
          </CardDropdown>
        );
      },
    },
  ], [isCollapsed, navigate, handleUpdateRole, confirmDelete]);

  const table = useAdvanceTable({
    data: roles || [],
    columns,
    sortable: true,
    pagination: true,
    perPage: 10,
  });

  return (
    <>
      <WidgetSectionTitle
        icon="user-shield"
        title="Roles"
        subtitle="Manage user roles and control access across the platform."
        transform="shrink-2"
        className="mb-3 mt-3"
      />

      <div className="d-flex justify-content-end mb-3">
        <Guard privilege={PRIV.CREATE_ROLES}>
          <IconButton
            variant="success"
            size="sm"
            icon="plus"
            onClick={() => setShowAddModal(true)}
            className="text-xs fw-bold"
            style={{ color: "#fff" }}
          >
            <span className="ms-1">Add Role</span>
          </IconButton>
        </Guard>
      </div>

      <div className="position-relative">
        {/* Local Loading Overlay */}
        {loading && (
          <div 
            className="position-absolute w-100 h-100 d-flex justify-content-center align-items-center" 
            style={{ 
                zIndex: 2, 
                backgroundColor: roles.length > 0 ? 'rgba(255, 255, 255, 0.6)' : '#fff',
                minHeight: 400 
            }}
          >
            <div className="text-center">
                <Spinner animation="border" variant="success" size="sm" />
                <div className="mt-2 text-500 fs-10 fw-medium">Updating roles...</div>
            </div>
          </div>
        )}

        {roles && roles.length > 0 ? (
          <AdvanceTableProvider {...table}>
            <div className="mx-n1 px-1">
              <AdvanceTable
                headerClassName="bg-light text-nowrap align-middle text-dark fs-10"
                rowClassName="align-middle white-space-nowrap fs-10"
                tableProps={{
                  bordered: true,
                  striped: true,
                  className: "mb-0 overflow-hidden",
                }}
              />
            </div>
            <div className="mt-3">
              <TablePaginationFooter
                page={table.getState().pagination.pageIndex}
                totalPages={table.getPageCount()}
                totalElements={roles.length}
                onPrev={table.getCanPreviousPage() ? () => table.previousPage() : undefined}
                onNext={table.getCanNextPage() ? () => table.nextPage() : undefined}
                pageSize={table.getState().pagination.pageSize}
                onPageSizeChange={(size) => table.setPageSize(size)}
                pageSizeOptions={[10, 20, 50, 100]}
              />
            </div>
          </AdvanceTableProvider>
        ) : !loading && (
          <div className="py-5 bg-white rounded-3 border border-200 text-center">
            <Empty description="No roles found" />
          </div>
        )}
      </div>

      <CreateOrUpdateRoleModal
        show={showAddModal}
        onHide={() => setShowAddModal(false)}
        onSave={addRole}
      />

      <CreateOrUpdateRoleModal
        show={showUpdateModal}
        onHide={() => setShowUpdateModal(false)}
        role={selectedRole}
        onSave={updateRole}
      />

      <ConfirmDeleteModal
        itemName="role"
        show={showDeleteModal}
        setShow={setShowDeleteModal}
        onConfirm={handleDelete}
        loading={deletingRole === roleToDelete}
      />
    </>
  );
}
